import React, { useEffect, useRef, useState } from 'react';
import Trash from '../../assets/svg/Trash.svg';
import Replace from '../../assets/svg/replaceicon.svg';
import AvatarCard from '../avatar-card';
import { FileChipInputProps } from './types';
import useToastify from '../../hooks/use-toastify';

export default function FileInput({
  name,
  placeholder,
  onSubmit,
  className,
  onClickItem,
  maxFileLength = 8, // Default maximum file limit
  logoURL,
  logoURLName,
  isReplaceIcon = false,
  showBrowseButton = true,
  files,
  setFiles
}: FileChipInputProps) {
  const [filePreviews, setFilePreviews] = useState<{ file: File; previewUrl: string }[]>([]);
  const { errorToast } = useToastify();
  const fileInputRef = useRef<HTMLInputElement>(null); // <-- Add ref for file input

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg'];

  // Generate file previews whenever files change
  useEffect(() => {
    const previews = files.map((file: any) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setFilePreviews(previews);

    return () => {
      previews.forEach((preview: any) => URL.revokeObjectURL(preview.previewUrl));
    };
  }, [files]);

  const validateFiles = (newFiles: File[]) => {
    if (files.length + newFiles.length > maxFileLength) {
      errorToast(
        `You can only upload a maximum of ${maxFileLength} files.`,
        { position: 'top-right' }
      );
      return [];
    }

    return newFiles.filter((newFile) => {
      if (!ALLOWED_FILE_TYPES.includes(newFile.type)) {
        errorToast(`${newFile.name} has an invalid file type.`, { position: 'top-right' });
        return false;
      }
      if (newFile.size > MAX_FILE_SIZE) {
        errorToast(`${newFile.name} exceeds the maximum file size of 5MB.`, { position: 'top-right' });
        return false;
      }
      if (files.some((file: any) => file.name === newFile.name && file.size === newFile.size)) {
        errorToast(`${newFile.name} is already uploaded.`, { position: 'top-right' });
        return false;
      }
      return true;
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const validFiles = validateFiles(newFiles);
      if (validFiles.length > 0) {
        const updatedFiles = [...files, ...validFiles];
        setFiles(updatedFiles);
        onSubmit?.(updatedFiles, false);
      }
      e.target.value = '';
    }
  };

  const handleRemoveFile = (fileToRemove: File) => {
    const updatedFiles = files.filter((file: any) => file !== fileToRemove);
    setFiles(updatedFiles);
    onSubmit?.(updatedFiles, true);
    if (fileInputRef.current && isReplaceIcon) {
      fileInputRef.current.click();
    }
  };

  return (
    <React.Fragment>
      <div className={`relative py-2 input input-md  rounded-basicRadius shadow-shadowAll w-full md:w-2/5 px-2 bg-white border border-feildBorder flex justify-between items-center ${className} ${(files?.length === 0 || maxFileLength > 1) && (logoURL === '' || showBrowseButton) ? 'visible' : 'hidden'}`}>
        <div className="  mt-0 font-aeonikReg text-disabledDark text-sm items-center">
          {placeholder || 'Choose from files'}
        </div>
        <label className="cursor-pointer text-white text-xs  py-2 input rounded-basicRadius w-auto px-6 bg-primary inline-block">
          {'Browse'}
          <input
            ref={fileInputRef}
            name={name}
            type="file"
            multiple
            placeholder={'Choose from file'}
            accept={ALLOWED_FILE_TYPES.join(',')}
            onChange={handleFileChange}
            className="hidden"
          />
        </label>
      </div>
      {filePreviews.length > 0 ? (
        <div
          className={`chips-container file-chips-container grid grid-cols-4 gap-x-2 ${(files?.length === 0 || maxFileLength > 1) && (logoURL === '' || showBrowseButton) ? '[&_>_div]:mt-2' : ''} mt-0   ${className}`}
        >
          {

            filePreviews.map(({ file, previewUrl }, index) => (
              <div
                key={index}
                className="flex justify-between w-full chip !p-2 !rounded-basicRadius !bg-white !border-feildBorder !border-0 items-center inner-file-input"
                onClick={() => onClickItem ? onClickItem(file) : {}}
              >
                <div className="">
                  <div className=" text-wrap-custom">
                    <AvatarCard imageUrl={previewUrl} title={file.name} subTitle={file.type} size="md" showTooltip={true} isViewMore={false} />
                  </div>
                </div>
                {!isReplaceIcon ? <div className="delete-btn">
                  <img src={Trash} alt="Remove file" className="cursor-pointer rounded-basicRadius border-2 border-feildBorde p-1.5 hover:border-primary" onClick={(e: any) => { e.stopPropagation(); handleRemoveFile(file); }} />
                </div> :
                  <div onClick={(e: any) => { e.stopPropagation(); handleRemoveFile(file); }} className="delete-btn cursor-pointer rounded-basicRadius border-2 border-feildBorder  p-1.5 hover:border-primary flex text-sm font-aeonikBold">
                    <img src={Replace} alt="Remove file" className="mr-1.5" />
                    <div className='pr-5'> Replace</div>
                  </div>}
              </div>
            ))}
        </div>
      ) :
        logoURL ?
          <div
            className={`chips-container file-chips-container grid grid-cols-4 gap-x-2 ${logoURL === '' || showBrowseButton ? '[&_>_div]:mt-2' : ''} mt-0   ${className}`}
          >
            <div
              className="flex justify-between w-full chip !p-2 !rounded-basicRadius !bg-white !border-feildBorder !border-0 items-center inner-file-input"
            >
              <div className="">
                <div className=" text-wrap-custom">
                  <AvatarCard imageUrl={logoURL} title={logoURLName || ''} subTitle={'image/png'} size="md" isViewMore={false} />
                </div>
              </div>
              <div onClick={(e: any) => { e.stopPropagation(); fileInputRef.current?.click(); }} className="delete-btn cursor-pointer rounded-basicRadius border-2 border-feildBorder  p-1.5 hover:border-primary flex text-sm font-aeonikBold">
                <img src={Replace} alt="Remove file" className="mr-1.5" /> Replace
              </div>
            </div></div> : ''
      }
    </React.Fragment>
  );
}